import { useState, useEffect } from 'react'
import { Prompt, withRouter } from 'react-router-dom'
import { Button } from '../../../iba-components/Button'
import { uploadAnalysis } from '../../api/analysis'
import { useTranslation } from 'react-i18next'
import { Input } from '../../../iba-components/Input'
import { Modal } from '../../../iba-components/Modal'
import './consultingAnalysisUpload.css'

const ConsultingAnalysisUpload = () => {
    const { t } = useTranslation()
    const [parameters, setParameters] = useState('')
    const [file, setFile] = useState(null)
    const [uploading, setUploading] = useState(false)
    const [processing, setProcessing] = useState(false)
    const [finishUploading, setFinishUploading] = useState(false)
    const [progress, setProgress] = useState(0)
    const [errorMsg, setErrorMsg] = useState('')

    // 確認モーダル用
    const [showConfirmModal, setShowConfirmModal] = useState(false)
    const [parsedParams, setParsedParams] = useState(null)

    // パラメーター分割
    const parseParameters = (parametersStr) => {
        const paramsSplit = parametersStr.split('\t')
        return {
            parameters_str: parametersStr,
            usage_or_client_folder: paramsSplit[0] || '',
            theme_name: paramsSplit[5] || '',
            patent_count: paramsSplit[6] || '',
            patent_period_start: paramsSplit[11] || '',
            patent_period_end: paramsSplit[12] || '',
            investment_period_start: paramsSplit[13] || '',
            investment_period_end: paramsSplit[14] || '',
            ipc_digit: paramsSplit[15] || '',
            one_cluster_one_ipc: paramsSplit[16] || '',
            is_cvc: paramsSplit[17] || ''
        }
    }

    // ファイル選択ハンドラ
    const handleFileChange = (e) => {
        const sel = e.target.files[0]
        if (sel && sel.name.endsWith('.zip')) {
            setFile(sel)
            setErrorMsg('')
        } else {
            setFile(null)
            setErrorMsg(t('consultingAnalysis.upload.error.selectZip'))
        }
    }

    const decodeHtmlEntities = (html) => {
        const txt = document.createElement('textarea')
        txt.innerHTML = html
        return txt.value
    }
    const validateParameters = (parametersStr) => {
        const paramsSplit = parametersStr.split('\t')

        const usage_or_client_folder = paramsSplit[0]
        const theme_name = paramsSplit[5]
        const patent_count = paramsSplit[6]
        const patent_period_start = paramsSplit[11]
        const patent_period_end = paramsSplit[12]
        const investment_period_start = paramsSplit[13]
        const investment_period_end = paramsSplit[14]
        const ipc_digit = paramsSplit[15]
        const one_cluster_one_ipc = paramsSplit[16]
        const is_cvc = paramsSplit[17]

        const periodRegex = /^\d{4}-(1H|2H)$/

        if (!usage_or_client_folder) return t('consultingAnalysis.upload.error.emptyClientFolder')
        if (!theme_name) return t('consultingAnalysis.upload.error.emptyThemeName')
        if (isNaN(patent_count.replace(',', ''))) return t('consultingAnalysis.upload.error.invalidPatentCount')
        if (!periodRegex.test(patent_period_start)) return t('consultingAnalysis.upload.error.invalidPatentPeriodStart')
        if (!periodRegex.test(patent_period_end)) return t('consultingAnalysis.upload.error.invalidPatentPeriodEnd')
        if (!periodRegex.test(investment_period_start))
            return t('consultingAnalysis.upload.error.invalidInvestmentPeriodStart')
        if (!periodRegex.test(investment_period_end))
            return t('consultingAnalysis.upload.error.invalidInvestmentPeriodEnd')
        if (ipc_digit !== '2(default)' && isNaN(ipc_digit)) return t('consultingAnalysis.upload.error.invalidIpcDigit')
        if (!['要', '不要'].includes(one_cluster_one_ipc))
            return t('consultingAnalysis.upload.error.invalidOneClusterOneIpc')
        if (!['①デフォルト', '②バリュ-チェ-ン横断', '①②両方'].includes(is_cvc))
            return t('consultingAnalysis.upload.error.invalidIsCvc')

        return null
    }

    // 送信ボタンハンドラ（確認モーダルを開く）
    const handleConfirm = () => {
        if (!parameters) {
            setErrorMsg(t('consultingAnalysis.upload.error.enterParameters'))
            return
        }
        if (!file) {
            setErrorMsg(t('consultingAnalysis.upload.error.selectZip'))
            return
        }

        const validationError = validateParameters(parameters)
        if (validationError) {
            setErrorMsg(validationError)
            return
        }

        const parsed = parseParameters(parameters)
        setParsedParams(parsed)
        setShowConfirmModal(true)
        setErrorMsg('')
    }

    // 実際のアップロード処理
    const handleSubmit = async () => {
        setShowConfirmModal(false)

        const formData = new FormData()
        formData.append('parameters_str', parameters)
        formData.append('file', file)

        setUploading(true)
        setProcessing(false)
        setProgress(0)
        setErrorMsg('')

        try {
            await uploadAnalysis(formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                onUploadProgress: (e) => {
                    if (!e.total) return
                    const p = Math.round((e.loaded * 100) / e.total)
                    setProgress(p)
                    if (p === 100) {
                        setProcessing(true)
                    }
                }
            })
            setFinishUploading(true)
            console.log('Upload & server processing completed')
        } catch (err) {
            // Incapsula（Imperva WAF）で強制切断される場合に空になる可能性が高い（6分タイムアウト、ファイルサイズ制限、攻撃判定された場合など）
            if (err.response?.data) {
                try {
                    setErrorMsg(JSON.parse(decodeHtmlEntities(err.response.data)).message)
                } catch {
                    setErrorMsg(err.response.data)
                }
            } else {
                setErrorMsg(t('consultingAnalysis.upload.error.networkError'))
            }
        } finally {
            setUploading(false)
            setProcessing(false)
        }
    }

    // タブクローズ・リロード時の警告制御
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            e.preventDefault()
            e.returnValue = ''
            return ''
        }
        if (uploading || processing) {
            window.addEventListener('beforeunload', handleBeforeUnload)
        } else {
            window.removeEventListener('beforeunload', handleBeforeUnload)
        }
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload)
        }
    }, [uploading, processing])

    // 確認モーダル
    const ConfirmModal = () => {
        if (!parsedParams) return null

        const paramItems = [
            { label: t('consultingAnalysis.list.usage_or_client_folder'), value: parsedParams.usage_or_client_folder },
            { label: t('consultingAnalysis.list.theme_name'), value: parsedParams.theme_name },
            { label: t('consultingAnalysis.list.patent_count'), value: parsedParams.patent_count },
            { label: t('consultingAnalysis.list.patent_period_start'), value: parsedParams.patent_period_start },
            { label: t('consultingAnalysis.list.patent_period_end'), value: parsedParams.patent_period_end },
            {
                label: t('consultingAnalysis.list.investment_period_start'),
                value: parsedParams.investment_period_start
            },
            { label: t('consultingAnalysis.list.investment_period_end'), value: parsedParams.investment_period_end },
            { label: t('consultingAnalysis.list.ipc_digit'), value: parsedParams.ipc_digit },
            { label: t('consultingAnalysis.list.one_cluster_one_ipc'), value: parsedParams.one_cluster_one_ipc },
            { label: t('consultingAnalysis.list.is_cvc'), value: parsedParams.is_cvc }
        ]

        return (
            <Modal visible={showConfirmModal} width={800} onCancel={() => setShowConfirmModal(false)}>
                <div className="confirm-modal-content">
                    <h3>{t('consultingAnalysis.upload.confirmTitle')}</h3>
                    <div className="confirm-params-list">
                        {paramItems.map((item, index) => (
                            <div key={index} className="confirm-param-item">
                                <span className="confirm-param-label">{item.label}:</span>
                                <span className="confirm-param-value">{item.value || '-'}</span>
                            </div>
                        ))}
                    </div>
                    <div className="confirm-modal-buttons">
                        <Button
                            label={t('consultingAnalysis.upload.back')}
                            onClick={() => setShowConfirmModal(false)}
                        />
                        <Button label={t('consultingAnalysis.upload.submit')} onClick={handleSubmit} />
                    </div>
                </div>
            </Modal>
        )
    }

    return (
        <div className="zip-upload-page">
            <Prompt when={uploading || processing} message={t('consultingAnalysis.upload.leavePageWarning')} />

            <h2>{t('consultingAnalysis.upload.pageTitle')}</h2>

            <div className="form-group">
                <Input
                    key="parameters_str"
                    label={t('consultingAnalysis.upload.parametersLabel')}
                    name="parameters_str"
                    required
                    width={560}
                    value={parameters}
                    placeholder={t('consultingAnalysis.upload.parametersPlaceholder')}
                    onChange={setParameters}
                />
            </div>

            <div className="form-group">
                <label htmlFor="input-file">{t('consultingAnalysis.upload.selectZipLabel')}</label>
                <div className="custom-file-input">
                    <input
                        id="input-file"
                        type="file"
                        accept=".zip"
                        onChange={handleFileChange}
                        disabled={uploading || processing || finishUploading}
                        style={{ display: 'none' }}
                    />
                    <Button
                        label={t('consultingAnalysis.upload.chooseFile')}
                        onClick={() => document.getElementById('input-file').click()}
                        disabled={uploading || processing || finishUploading}
                    />
                    <span className="file-name">
                        {file ? file.name : t('consultingAnalysis.upload.noFileSelected')}
                    </span>
                </div>
                <p className="file-input-note">{t('consultingAnalysis.upload.fileSelectNote')}</p>
            </div>

            {uploading && progress < 100 && (
                <div className="upload-progress">
                    <progress value={progress} max="100" />
                    <span>{progress}%</span>
                </div>
            )}

            {processing && (
                <div className="processing">
                    <div className="spinner" />
                    <span>{t('consultingAnalysis.upload.processing')}</span>
                </div>
            )}

            {finishUploading && (
                <div className="processing">
                    <span className="finish-uploading">{t('consultingAnalysis.upload.uploadComplete')}</span>
                </div>
            )}

            {errorMsg && <div className="error-message">{errorMsg}</div>}

            <div className="button-area">
                <Button
                    label={
                        uploading || processing
                            ? t('consultingAnalysis.upload.processingButton')
                            : t('consultingAnalysis.upload.submit')
                    }
                    disabled={uploading || processing || finishUploading}
                    onClick={handleConfirm}
                />
            </div>

            <ConfirmModal />
        </div>
    )
}

export default withRouter(ConsultingAnalysisUpload)
